import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminBorderUprightSpanMapping } from "../../../../../http/border/types/uprightSpanMappings";
import { useUprightSpanMappingsApi } from "../../../../../http/border/useUprightSpanMappings";
import { PaginationQuery } from "../../../../../types";
import { itemLabel } from "../../../../../utils";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import CreateUprightSpanMappingModalDialogButton from "./CreateUprightSpanMappingModalDialogButton";
import UprightSpanMappingForm from "./UprightSpanMappingForm";

const formatNumber = (value: number | null | undefined, unit?: string) => {
  if (value === null || value === undefined) {
    return "";
  }

  return unit ? `${value} ${unit}` : value;
};

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const UprightSpanMappings: FC<Props> = ({ active }) => {
  const { borderSystemId } = useParams<{ borderSystemId: string }>();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "uprightSpanMappings" });
  const aborter = useRef(new AbortController());
  const uprightSpanMappingsApi = useUprightSpanMappingsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "uprightSpanMappings");

  const isActiveTab = active ?? true;
  const [itemToEdit, setItemToEdit] = useState<AdminBorderUprightSpanMapping>();
  const [itemToDelete, setItemToDelete] = useState<AdminBorderUprightSpanMapping>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = uprightSpanMappingsApi.useAdminUprightSpanMappings(
    borderSystemId!,
    query,
    aborter.current.signal,
  );

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.uprightSpanMappings.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.uprightSpanMappings.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateUprightSpanMappingModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminBorderUprightSpanMapping>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.normatives.singular}</th>
            <th align="left">{i18n.translation.uprights.singular}</th>
            <th align="left">{i18n.translation.substructures.singular}</th>
            <th align="left">{i18n.translation.bases.singular}</th>
            <th align="left">{i18n.translation.ballasts.singular}</th>
            <th align="right">{i18n.translation.common.nrBallastTerminal}</th>
            <th align="right">{i18n.translation.common.nrBallastIntermediate}</th>
            <th align="right">{i18n.translation.common.nrRivets}</th>
            <th align="right">{i18n.translation.common.span}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <Table.Td noWrap>{item.systemNormative.normative.name}</Table.Td>
            <Table.Td noWrap>{itemLabel(item.upright.item)}</Table.Td>
            <td>{item.substructure?.name}</td>
            <Table.Td noWrap>{itemLabel(item.base?.item)}</Table.Td>
            <Table.Td noWrap>{itemLabel(item.ballast?.item)}</Table.Td>
            <td align="right">{formatNumber(item.nrBallastTerminal)}</td>
            <td align="right">{formatNumber(item.nrBallastInt)}</td>
            <td align="right">{formatNumber(item.nrRivets)}</td>
            <td align="right">{formatNumber(item.span, "mm")}</td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.uprightSpanMappings.edit}>
          <UprightSpanMappingForm uprightSpanMapping={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.uprightSpanMappings.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                uprightSpanMappingsApi
                  .deleteAdminUprightSpanMapping(borderSystemId!, itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.uprightSpanMappings.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default UprightSpanMappings;
